import { ErrorBoundary } from "@/components/ui/error-boundary";
import { Toaster } from "@/components/ui/toaster";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { Router } from "@/lib/Router";
import { useAuth } from "@/hooks/use-auth";
import { useUserLanguage } from "@/hooks/use-user-language";
import { AuthLoading, ErrorAlert } from "@/components/ui/loading-states";
import { WelcomeFlow } from "./components/onboarding/welcome-flow";
import { useState, useEffect } from "react";

import { useLocation, Redirect } from "wouter";
import { isNativeBuild } from "@/lib/config";
import { useNativeShell } from "@/hooks/use-native-shell";

function AuthenticatedApp() {
  useNativeShell();
  const [location] = useLocation();
  // Only /app/* routes require authentication; everything else is public
  // (unknown routes will render the 404 page via the Router's catch-all)
  const isAppRoute = location.startsWith('/app');
  // Firebase email-link callback routes must bypass the onboarding gate
  // so the CompleteUpgrade page can consume the oobCode. Otherwise on a
  // new device/browser the gate short-circuits to WelcomeFlow and the
  // magic link silently fails.
  const isAuthCallbackRoute =
    location.startsWith('/app/auth/complete-upgrade') ||
    location.startsWith('/app/auth/complete-login');

  const {
    user,
    loading,
    authError,
    retryAuth,
    isFirebaseConfigured,
    isInitialized,
    migrationCompleted,
    getSessionInfo
  } = useAuth();
  const { userLanguage, updateLanguage } = useUserLanguage();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState<{
    completed: boolean;
    migrated: string[];
    errors: string[];
  }>();

  // Google Analytics: track SPA page views on route change
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_path: location });
    }
  }, [location]);

  // Check if onboarding is needed
  useEffect(() => {
    if (user && isInitialized && migrationCompleted) {
      const savedLanguage = localStorage.getItem('vyzle-chat-language');
      const hasCompletedOnboarding = localStorage.getItem('vyzle-chat-onboarded');

      // Show onboarding if language not set OR onboarding not completed
      if (!savedLanguage || !hasCompletedOnboarding) {
        setShowOnboarding(true);
      }
    }
  }, [user, isInitialized, migrationCompleted]);

  // Handle onboarding completion
  const handleOnboardingComplete = async (data: { language: string; name: string }) => {
    console.log('Onboarding completed with:', data);

    try {
      await updateLanguage(data.language);

      // Store user's name for personalization
      localStorage.setItem('vyzle-chat-user-name', data.name);
      localStorage.setItem('vyzle-chat-onboarded', 'true');

      setShowOnboarding(false);
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  };

  // Determine current auth status for loading screen
  const getAuthStatus = () => {
    if (!isInitialized) return 'initializing';
    if (!migrationCompleted) return 'migrating';
    if (loading && !user) return 'authenticating';
    if (user && loading) return 'syncing';
    return 'fallback';
  };

  // The native shell loads index.html at "/", which would open the app on the
  // marketing landing page. Send it straight to the chat app instead — a
  // wrapper that opens on a marketing site is the archetypal App Store
  // Guideline 4.2 rejection. Only the bare root is redirected, so /support,
  // /privacy-policy and the deep links keep working inside the app.
  if (isNativeBuild && location === "/") {
    return <Redirect to="/app" replace />;
  }

  // Allow public routes (and unknown routes → 404) to render without auth
  if (!isAppRoute) {
    return (
      <ErrorBoundary>
        <Router />
      </ErrorBoundary>
    );
  }

  // Capture invite code from URL before auth gating intercepts it
  // This ensures new users going through onboarding don't lose the invite
  const joinMatch = location.match(/^\/app\/join\/(.+)$/);
  if (joinMatch) {
    localStorage.setItem("pending_invite_code", joinMatch[1]);
  }

  // Enhanced loading state with migration info
  if (loading || !isInitialized || !migrationCompleted) {
    return (
      <AuthLoading
        status={getAuthStatus()}
        isFirebaseConfigured={isFirebaseConfigured}
        migrationStatus={migrationStatus}
      />
    );
  }

  // Enhanced error handling
  if (authError) {
    const sessionInfo = getSessionInfo();

    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          <ErrorAlert
            error={authError}
            onRetry={retryAuth}
            type="auth"
            className="mb-4"
          />

          {/* Debug info for troubleshooting */}
          <div className="bg-muted/30 rounded-lg p-4 text-sm">
            <h4 className="font-medium mb-2">Troubleshooting Information:</h4>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Firebase: {isFirebaseConfigured ? '✅ Configured' : '❌ Not configured'}</li>
              <li>• Last synced user: {sessionInfo.lastSyncedUser || 'None'}</li>
              <li>• App version: {sessionInfo.appVersion || 'Unknown'}</li>
              <li>• Last migration: {sessionInfo.lastMigration ? new Date(sessionInfo.lastMigration).toLocaleString() : 'Never'}</li>
            </ul>

            <div className="mt-4 space-y-2">
              <button
                onClick={retryAuth}
                className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                Try Again
              </button>

              <div className="text-xs text-muted-foreground space-y-1">
                <p className="font-medium">Additional troubleshooting:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Check your internet connection</li>
                  {!isFirebaseConfigured && <li>Set up Firebase configuration (see README)</li>}
                  <li>Clear browser cache and reload</li>
                  <li>Try incognito/private browsing mode</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No authenticated user — show onboarding for new users, spinner for returning users
  // who are mid-authentication (e.g. network delay re-signing in).
  if (!user) {
    // Auth-callback routes self-handle the no-user case (login mode creates
    // a user via signInWithEmailLink). Let the router mount them.
    if (isAuthCallbackRoute) {
      return (
        <ErrorBoundary>
          <Router />
        </ErrorBoundary>
      );
    }

    const isOnboarded =
      !!localStorage.getItem('vyzle-chat-onboarded') &&
      !!localStorage.getItem('vyzle-chat-user-name');

    if (!isOnboarded) {
      // New user — show WelcomeFlow which calls signIn() after name is collected
      return (
        <WelcomeFlow
          onComplete={handleOnboardingComplete}
          isFirebaseConfigured={isFirebaseConfigured}
        />
      );
    }

    // Returning user whose Firebase session is still being restored
    return (
      <AuthLoading
        status="fallback"
        isFirebaseConfigured={isFirebaseConfigured}
      />
    );
  }

  // Show onboarding for an already-authenticated user who hasn't finished setup
  // (e.g. they cleared localStorage but still have a Firebase session) — but
  // never when the user is mid-auth-callback, or the magic link silently fails.
  if (showOnboarding && !isAuthCallbackRoute) {
    return (
      <WelcomeFlow
        onComplete={handleOnboardingComplete}
        isFirebaseConfigured={isFirebaseConfigured}
      />
    );
  }

  // Main application
  return (
    <ErrorBoundary>
      <Router />
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <ThemeProvider defaultTheme="system" storageKey="vyzle-chat-theme">
      <AuthenticatedApp />
      {/* App Version Badge */}
      {/* <div
        className="fixed bottom-2 right-2 px-2 py-1 text-xs rounded-md bg-muted/60 text-muted-foreground backdrop-blur-sm"
        aria-label="Application Version"
      >
        v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0'}
      </div> */}
      <Toaster />
    </ThemeProvider>
  );
}
